import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Toolbar,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const COMPANY = 'KT Mountain Ranch Quarter Horses LLC';
const CONTACT_EMAIL = 'privacy@ktmranch.com';
const EFFECTIVE_DATE = 'June 26, 2026';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {children}
    <Divider sx={{ mt: 3 }} />
  </Box>
);

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            {COMPANY}
          </Typography>
          <Button color="inherit" variant="outlined" onClick={() => navigate('/login')}>
            Sign In
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Effective date: {EFFECTIVE_DATE}
        </Typography>

        <Typography variant="body1" paragraph>
          {COMPANY} ("we," "our," or "us") operates the KTM Ranch Breeding platform. This Privacy
          Policy explains how we collect, use, and protect information when you use our service.
        </Typography>

        <Section title="1. Information We Collect">
          <Typography variant="body1" paragraph>
            <strong>Account information:</strong> When you register, we collect your name, email
            address, username, and password (stored as a secure hash).
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Ranch data:</strong> Information you enter into the platform, including horse
            records, breeding records, expense and income entries, appointment logs, veterinary
            records, and related documents or photos.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Usage data:</strong> Basic server logs (IP address, browser type, pages
            accessed, timestamps) used for security and performance monitoring.
          </Typography>
        </Section>

        <Section title="2. How We Use Your Information">
          <Typography variant="body1" paragraph>
            We use the information we collect to:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mt: 0 }}>
            <Typography component="li" variant="body1">Provide, operate, and maintain the platform</Typography>
            <Typography component="li" variant="body1">Process and display your ranch management data</Typography>
            <Typography component="li" variant="body1">Authenticate your identity and secure your account</Typography>
            <Typography component="li" variant="body1">Send account-related communications (e.g. password resets)</Typography>
            <Typography component="li" variant="body1">Diagnose technical issues and improve platform performance</Typography>
          </Box>
        </Section>

        <Section title="3. Data Storage and Security">
          <Typography variant="body1" paragraph>
            Your data is stored on secure, access-controlled servers. All data in transit is
            encrypted via SSL/TLS. We implement reasonable technical and organizational measures
            to protect your information against unauthorized access, loss, or disclosure.
          </Typography>
          <Typography variant="body1" paragraph>
            Receipt images and horse photos are stored as files on the server and are only
            accessible through authenticated requests.
          </Typography>
        </Section>

        <Section title="4. Data Sharing">
          <Typography variant="body1" paragraph>
            We do not sell, rent, or share your personal or ranch data with third parties for
            marketing purposes.
          </Typography>
          <Typography variant="body1" paragraph>
            We use the following third-party services to operate the platform:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mt: 0 }}>
            <Typography component="li" variant="body1">
              <strong>Anthropic (Claude API):</strong> Receipt images and natural-language queries
              are sent to Anthropic's API for AI-powered features (receipt scanning, ranch
              assistant). Data is processed per Anthropic's privacy policy
              and is not used to train their models.
            </Typography>
            <Typography component="li" variant="body1">
              <strong>ElevenLabs (optional):</strong> Text is sent for voice synthesis in the
              ranch assistant feature if voice output is enabled.
            </Typography>
          </Box>
        </Section>

        <Section title="5. Data Retention">
          <Typography variant="body1" paragraph>
            Your data is retained for as long as your account is active. If you request account
            deletion, we will delete your personal information and ranch data within 30 days,
            except where retention is required by law.
          </Typography>
        </Section>

        <Section title="6. Your Rights">
          <Typography variant="body1" paragraph>
            You have the right to access, correct, export, or delete your data. To exercise any
            of these rights, contact us at{' '}
            <Box component="a" href={`mailto:${CONTACT_EMAIL}`} sx={{ color: 'primary.main' }}>
              {CONTACT_EMAIL}
            </Box>
            .
          </Typography>
        </Section>

        <Section title="7. Changes to This Policy">
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. We will notify users of material
            changes by updating the effective date above. Continued use of the platform after
            changes constitutes acceptance of the revised policy.
          </Typography>
        </Section>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body1">
            <strong>Contact:</strong>{' '}
            <Box component="a" href={`mailto:${CONTACT_EMAIL}`} sx={{ color: 'primary.main' }}>
              {CONTACT_EMAIL}
            </Box>
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {COMPANY}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
